import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

/**
 * Horizontal scroller with edge fades: while content is hidden beyond the
 * left or right edge a soft shadow sits on that edge, so it's obvious a
 * wide table continues (the same affordance the data sheet grid uses).
 * `style` goes on the outer box (border, radius), `scrollStyle` on the
 * scrolling element (max height, vertical overflow).
 */
export function ScrollShadow({
  children,
  style,
  scrollStyle,
  className,
}: {
  children: ReactNode;
  style?: CSSProperties;
  scrollStyle?: CSSProperties;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hint, setHint] = useState({ left: false, right: false });

  const update = () => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = { left: el.scrollLeft > 2, right: max - el.scrollLeft > 2 };
    setHint((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  };

  // Re-measure after every render (content changes) and on any resize of
  // the scroller or its content.
  useEffect(update);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={`scroll-shadow${className ? ` ${className}` : ""}`} style={style}>
      <div ref={ref} className="scroll-shadow-inner" style={scrollStyle} onScroll={update}>
        {children}
      </div>
      {hint.left && <div className="scroll-shadow-fade scroll-shadow-fade--left" aria-hidden="true" />}
      {hint.right && <div className="scroll-shadow-fade scroll-shadow-fade--right" aria-hidden="true" />}
    </div>
  );
}
